import { useRef } from 'react';
import { useElementWidth } from '@/hooks/useElementWidth';
import { cn } from '@/lib/utils';
import { REFETCH_FADE_TRANSITION_CLASS, REFETCHING_OPACITY_CLASS } from '@/styles/refetchFade';
import { StatCard, StatCardSkeleton, type StatCardDensity } from './StatCard';
import { getStatCardWidths, type StatCardText, type StatCardWidths } from './statCardWidths';

export interface LabeledStat {
  key: string;
  label: string;
  value: number | string | undefined;
  unit?: string | undefined;
  hint?: string | undefined;
}

interface StatRow {
  density: StatCardDensity;
  columnCount: number;
  gridTemplateColumns: string;
}

const ROW_GAP_PX = 12;
const FEW_CARDS_MAX_COUNT = 2;
const FEW_CARDS_SLOT_COUNT = 4;
const MIN_WRAPPED_COLUMN_COUNT = 2;
const TWO_COLUMNS = 2;
const DEFAULT_SKELETON_CARD_COUNT = 4;
const SKELETON_CARD_TEXT: StatCardText = { label: '', value: undefined };
const LAST_ODD_CARD_CLASS = 'col-span-2';

const sumPx = (widthsPx: number[]): number =>
  widthsPx.reduce((totalPx, widthPx) => totalPx + widthPx, 0);

function toColumnTemplate(
  cardColumns: string[],
  emptySlotCount: number,
  emptySlotWeightPx: number,
): string {
  const emptyColumns = Array.from(
    { length: emptySlotCount },
    () => `minmax(0, ${Math.round(emptySlotWeightPx)}fr)`,
  );
  return [...cardColumns, ...emptyColumns].join(' ');
}

function getWrappedColumnCount(cardWidths: StatCardWidths[], rowWidthPx: number): number {
  const widestMinPx = Math.max(...cardWidths.map((widths) => widths.compactMinPx));
  const fittingCount = Math.floor((rowWidthPx + ROW_GAP_PX) / (widestMinPx + ROW_GAP_PX));
  const columnCount = Math.min(Math.max(fittingCount, MIN_WRAPPED_COLUMN_COUNT), cardWidths.length);
  const rowCount = Math.ceil(cardWidths.length / columnCount);
  return Math.ceil(cardWidths.length / rowCount);
}

function getStatRow(
  cardWidths: StatCardWidths[],
  slotCount: number,
  rowWidthPx: number | undefined,
): StatRow {
  const emptySlotCount = slotCount - cardWidths.length;
  const gapsPx = (slotCount - 1) * ROW_GAP_PX;
  const regularWidthsPx = cardWidths.map((widths) => widths.regularPx);
  const averageRegularPx = sumPx(regularWidthsPx) / cardWidths.length;
  const regularRowPx = sumPx(regularWidthsPx) + emptySlotCount * averageRegularPx + gapsPx;

  if (rowWidthPx === undefined || regularRowPx <= rowWidthPx) {
    return {
      density: 'regular',
      columnCount: slotCount,
      gridTemplateColumns: toColumnTemplate(
        regularWidthsPx.map((widthPx) => `minmax(0, ${Math.round(widthPx)}fr)`),
        emptySlotCount,
        averageRegularPx,
      ),
    };
  }

  const compactMinRowPx = sumPx(cardWidths.map((widths) => widths.compactMinPx)) + gapsPx;
  if (compactMinRowPx <= rowWidthPx) {
    const averageCompactPx =
      sumPx(cardWidths.map((widths) => widths.compactPx)) / cardWidths.length;
    return {
      density: 'compact',
      columnCount: slotCount,
      gridTemplateColumns: toColumnTemplate(
        cardWidths.map(
          (widths) =>
            `minmax(${Math.round(widths.compactMinPx)}px, ${Math.round(widths.compactPx)}fr)`,
        ),
        emptySlotCount,
        averageCompactPx,
      ),
    };
  }

  const columnCount = getWrappedColumnCount(cardWidths, rowWidthPx);
  return {
    density: 'compact',
    columnCount,
    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
  };
}

interface StatCardRowProps {
  stats: LabeledStat[];
  cardCount: number;
  slotCount: number;
  showsSkeletonCards: boolean;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  isFetching?: boolean | undefined;
}

function StatCardRow({
  stats,
  cardCount,
  slotCount,
  showsSkeletonCards,
  isLoading,
  isError,
  isFetching,
}: StatCardRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const rowWidthPx = useElementWidth(rowRef);
  const cardTexts: StatCardText[] = showsSkeletonCards
    ? Array.from({ length: cardCount }, () => SKELETON_CARD_TEXT)
    : stats;
  const row = getStatRow(cardTexts.map(getStatCardWidths), slotCount, rowWidthPx);
  const lastOddIndex = row.columnCount === TWO_COLUMNS && cardCount % 2 === 1 ? cardCount - 1 : -1;
  const getCardClassName = (index: number) => cn(index === lastOddIndex && LAST_ODD_CARD_CLASS);

  return (
    <div
      ref={rowRef}
      className={cn(
        'grid gap-3',
        REFETCH_FADE_TRANSITION_CLASS,
        isFetching && REFETCHING_OPACITY_CLASS,
      )}
      style={{ gridTemplateColumns: row.gridTemplateColumns }}
    >
      {showsSkeletonCards
        ? Array.from({ length: cardCount }, (_, index) => (
            <StatCardSkeleton
              key={`stat-skeleton-${index}`}
              density={row.density}
              className={getCardClassName(index)}
            />
          ))
        : stats.map((stat, index) => (
            <StatCard
              key={stat.key}
              label={stat.label}
              value={stat.value}
              unit={stat.unit}
              hint={stat.hint}
              isLoading={isLoading}
              isError={isError}
              density={row.density}
              className={getCardClassName(index)}
            />
          ))}
    </div>
  );
}

interface StatCardGridProps {
  stats: LabeledStat[];
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  isFetching?: boolean | undefined;
  skeletonCardCount?: number | undefined;
  layoutCardCount?: number | undefined;
}

export function StatCardGrid({
  stats,
  isLoading,
  isError,
  isFetching,
  skeletonCardCount = DEFAULT_SKELETON_CARD_COUNT,
  layoutCardCount,
}: StatCardGridProps) {
  const showsSkeletonCards = isLoading === true && stats.length === 0;
  const cardCount = showsSkeletonCards ? skeletonCardCount : stats.length;
  if (cardCount === 0) return null;

  const slotCount = Math.max(
    cardCount,
    layoutCardCount ?? 0,
    cardCount <= FEW_CARDS_MAX_COUNT ? FEW_CARDS_SLOT_COUNT : 0,
  );

  return (
    <StatCardRow
      stats={stats}
      cardCount={cardCount}
      slotCount={slotCount}
      showsSkeletonCards={showsSkeletonCards}
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
    />
  );
}
